import { getConfig } from "@bidbattles/bloks/config";
import type { UserSubscription } from "@bidbattles/bloks/lib/rapace";
import { isBefore } from "date-fns";

const currencies = {
	EUR: {
		symbol: "€",
		locale: "en-US",
	},
	LOYALTY_POINTS: {
		symbol: "BLC",
		locale: "en-US",
	},
} as const;

export function getSymbol(currency: keyof typeof currencies) {
	return currencies[currency].symbol;
}

export function formatCurrency(
	currency: keyof typeof currencies,
	amount: number | string,
	options?: Omit<Intl.NumberFormatOptions, "style" | "currency">,
) {
	const amountNum =
		typeof amount === "number" ? amount / 100 : Number.parseFloat(amount);

	const str = new Intl.NumberFormat(undefined, {
		style: "currency",
		currency: currency === "LOYALTY_POINTS" ? "USD" : currency,
		minimumFractionDigits: 2,
		currencyDisplay: currency === "LOYALTY_POINTS" ? "code" : undefined,
		...options,
	}).format(amountNum);

	if (currency === "LOYALTY_POINTS") {
		return str.replace("USD", currencies.LOYALTY_POINTS.symbol);
	}

	return str;
}

const VAT = 0.21; // 21%
export function calculateVAT(amount: number) {
	return {
		percentage: VAT,
		vat: amount * VAT,
	};
}

const SERVICE_FEE = 0.11; // 11%
export function calculateEntryPrice(price: number, seats: number) {
	return (price / seats) * (1 + SERVICE_FEE);
}

export function calculateItemPrice(entryPrice: number, seats: number) {
	return (entryPrice * seats) / (1 + SERVICE_FEE);
}

export function exponentialBackoff(
	base: number,
	attempt: number,
	max?: number,
) {
	return Math.min(max ?? Number.POSITIVE_INFINITY, base * 2 ** (attempt - 1));
}

export function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}

export function getOpenSeats(battle: {
	open_seats: number;
	total_seats: number;
}) {
	const seats = battle.total_seats - battle.open_seats;
	const length = String(battle.total_seats).length;
	return seats.toString().padStart(length, "0");
}

function expiryPassed(date?: Date) {
	if (!date) {
		return false;
	}
	return isBefore(date, new Date());
}

export function hadPremiumSubscription(sub: UserSubscription) {
	// canceled/expired premium subs don't drop back to the free tier
	if (sub.tier === "FREE") {
		return false;
	}

	if (expiryPassed(sub.expiry)) {
		return true;
	}

	return sub.status === "expired" || hasCancelledPremiumSubscription(sub);
}

export function hasPremiumSubscription(sub: UserSubscription) {
	if (expiryPassed(sub.expiry)) {
		return false;
	}

	return (
		sub.tier !== "FREE" &&
		sub.status !== "expired" &&
		!hasCancelledPremiumSubscription(sub)
	);
}

export function hasCancelledPremiumSubscription(sub: UserSubscription) {
	return sub.status === "canceled";
}
